/**
 * EZ Septic Pros service area — the nine North Carolina counties served.
 * Shared between client (form validation warnings) and server (lead flagging).
 */
export const SERVICE_AREA_STATE = "NC";

export const SERVICE_AREA_COUNTIES = [
  "Mecklenburg",
  "Cabarrus",
  "Iredell",
  "Rowan",
  "Stanly",
  "Davie",
  "Davidson",
  "Gaston",
  "Union",
] as const;

export type ServiceAreaCounty = (typeof SERVICE_AREA_COUNTIES)[number];

/** Normalize a county string like "Mecklenburg County" → "mecklenburg". */
function normalizeCounty(county: string): string {
  return county
    .toLowerCase()
    .replace(/\s+county\s*$/i, "")
    .trim();
}

export interface ServiceAreaCheck {
  /** true = in area, false = out of area, null = could not determine */
  inServiceArea: boolean | null;
  matchedCounty?: ServiceAreaCounty;
  reason?: string;
}

/**
 * Check whether a county + state pair falls inside the service area.
 * Returns null verdict when county/state are missing (do not block the lead).
 */
export function checkServiceArea(county?: string | null, state?: string | null): ServiceAreaCheck {
  if (!county && !state) return { inServiceArea: null, reason: "No location details" };
  if (state && state.toUpperCase() !== SERVICE_AREA_STATE) {
    return { inServiceArea: false, reason: `Outside North Carolina (${state.toUpperCase()})` };
  }
  if (!county) return { inServiceArea: null, reason: "County unknown" };
  const normalized = normalizeCounty(county);
  const matched = SERVICE_AREA_COUNTIES.find((c) => c.toLowerCase() === normalized);
  if (matched) return { inServiceArea: true, matchedCounty: matched };
  return { inServiceArea: false, reason: `${county} is outside our nine-county service area` };
}

/** Human-readable list for UI copy: "Mecklenburg, Cabarrus, ... & Union" */
export const SERVICE_AREA_LABEL = `${SERVICE_AREA_COUNTIES.slice(0, -1).join(", ")} & ${
  SERVICE_AREA_COUNTIES[SERVICE_AREA_COUNTIES.length - 1]
} Counties, NC`;
